import { FC, useState, useEffect } from 'react'
import { Download, X, Share2, Plus } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isIOS(): boolean {
  const ua = navigator.userAgent
  return /iphone|ipad|ipod/.test(ua.toLowerCase()) && !(window as unknown as Record<string, unknown>).MSStream
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone: boolean }).standalone === true
}

export const InstallPrompt: FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSBanner, setShowIOSBanner] = useState(false)
  const [showAndroidBanner, setShowAndroidBanner] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (isStandalone() || dismissed) return

    const DISMISSED_KEY = 'ecofarte_install_dismissed'
    if (localStorage.getItem(DISMISSED_KEY)) return

    if (isIOS()) {
      setShowIOSBanner(true)
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowAndroidBanner(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [dismissed])

  const dismiss = () => {
    setShowIOSBanner(false)
    setShowAndroidBanner(false)
    setDismissed(true)
    localStorage.setItem('ecofarte_install_dismissed', 'true')
  }

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setShowAndroidBanner(false)
    }
    setDeferredPrompt(null)
  }

  if (isStandalone()) return null

  if (showIOSBanner) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 lg:hidden">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 max-w-md mx-auto">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#7e3e98] flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Instalar eCOFARTE</p>
                <p className="text-xs text-gray-500">Accede más rápido desde tu pantalla de inicio</p>
              </div>
            </div>
            <button onClick={dismiss} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                <Share2 className="w-3 h-3 text-white" />
              </div>
              <p className="text-xs text-gray-700">
                Pulsa el botón <span className="font-medium">Compartir</span> de Safari
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                <Plus className="w-3 h-3 text-gray-600" />
              </div>
              <p className="text-xs text-gray-700">
                Desplázate y pulsa <span className="font-medium">"Añadir a pantalla de inicio"</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showAndroidBanner && deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 lg:hidden">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 max-w-md mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#7e3e98] flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">Instalar eCOFARTE</p>
            <p className="text-xs text-gray-500 truncate">Añade la app a tu pantalla de inicio</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={dismiss}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              Ahora no
            </button>
            <button
              onClick={handleAndroidInstall}
              className="px-4 py-1.5 text-xs font-medium text-white bg-[#7e3e98] rounded-lg hover:bg-[#6b3380] transition-colors"
            >
              Instalar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
